import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Platform, type TextInput } from 'react-native';
import {
  activeMentionQuery,
  applyMentionPick,
  deserializeMentions,
  hasMentionTokens,
  matchMentionCandidates,
  mergeMentionResults,
  reconcilePicks,
  serializeMentions,
  shouldShowMentionEmpty,
  type MentionCandidate,
  type MentionPick,
} from '../utils/chatMentions';

export interface MentionDraft {
  /** Подсказки к набираемому `@имени`; пусто — меню не показывать. */
  matches: MentionCandidate[];
  /** Набирается имя, которого в сессии нет, — «никого не нашлось». */
  showEmpty: boolean;
  /** CORE-1684 — поиск по платформе ещё идёт. */
  searching: boolean;
  pick: (candidate: MentionCandidate) => void;
  /** Черновик с метками упоминаний — то, что уходит в хаб. */
  serialize: () => string;
}

/**
 * CORE-1421 — упоминания поверх черновика, которым владеет экран. Текст
 * остаётся у экрана (он же очищает поле после отправки и возвращает
 * отклонённое), здесь — только какие `@Имя` в нём выбраны из подсказок.
 */
/** CORE-1684 — сколько ждать после последней буквы, прежде чем спросить сервер. */
const SEARCH_DEBOUNCE_MS = 250;

export function useMentionDraft(
  text: string,
  setText: (text: string) => void,
  candidates: readonly MentionCandidate[],
  /** Поле ввода: после выбора подсказки фокус и курсор возвращаются в конец. */
  inputRef?: RefObject<TextInput | null>,
  /**
   * CORE-1684 — поиск по всей платформе (по нику и имени). Участники чата
   * отвечают сразу, найденные встают следом. Нет — подсказки только местные.
   */
  search?: (query: string) => Promise<MentionCandidate[]>,
): MentionDraft {
  const [picks, setPicks] = useState<MentionPick[]>([]);
  const prevRef = useRef(text);
  const [remote, setRemote] = useState<{ query: string; items: MentionCandidate[] } | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const prev = prevRef.current;
    if (prev === text) return;
    // Отклонённое модерацией сообщение вернули в поле уже с метками —
    // разворачиваем обратно в `@Имя`, упоминания остаются выбранными.
    if (hasMentionTokens(text)) {
      const draft = deserializeMentions(text);
      prevRef.current = draft.text;
      setPicks(draft.picks);
      setText(draft.text);
      return;
    }
    prevRef.current = text;
    setPicks((current) => reconcilePicks(prev, text, current));
  }, [text, setText]);

  const active = activeMentionQuery(text, picks);
  const query = active?.query ?? null;

  // Поиск — с отступом после последней буквы и только по непустому запросу;
  // ответ на устаревший запрос отбрасывается, чтобы не мигали чужие имена.
  useEffect(() => {
    if (!search || query === null || query.trim() === '' || /\s$/.test(query)) {
      setSearching(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      search(query)
        .then((items) => { if (alive) setRemote({ query, items }); })
        .catch(() => { if (alive) setRemote({ query, items: [] }); })
        .finally(() => { if (alive) setSearching(false); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [search, query]);

  const matches = useMemo(() => {
    if (query === null) return [];
    const local = candidates.length > 0 ? matchMentionCandidates(candidates, query) : [];
    if (!search) return local;
    const found = remote && remote.query === query ? remote.items : [];
    return mergeMentionResults(local, found);
  }, [query, candidates, search, remote]);
  const resolved = !search || query === null || query.trim() === '' || (!searching && remote?.query === query);
  const showEmpty = !!active && (candidates.length > 0 || !!search) && resolved
    && shouldShowMentionEmpty(active.query, matches);

  const pick = useCallback((candidate: MentionCandidate) => {
    if (!active) return;
    const next = applyMentionPick(text, picks, active.start, candidate);
    // Эффект выше не должен принять нашу же замену за правку поверх упоминания.
    prevRef.current = next.text;
    setPicks(next.picks);
    setText(next.text);
    // Нажатие на подсказку уводит фокус из поля, и на вебе курсор возвращался
    // в начало: следующее слово печаталось ПЕРЕД текстом. Ставим его в конец.
    requestAnimationFrame(() => {
      const input = inputRef?.current as (TextInput & { setSelectionRange?: (a: number, b: number) => void }) | null | undefined;
      input?.focus();
      if (Platform.OS === 'web') input?.setSelectionRange?.(next.text.length, next.text.length);
    });
  }, [active?.start, text, picks, setText, inputRef]);

  const serialize = useCallback(() => serializeMentions(text, picks), [text, picks]);

  return { matches, showEmpty, searching, pick, serialize };
}
